/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IAny, IGeneric, msdyn365Commerce, IDictionary, IModule } from '@msdyn365-commerce/core-internal';
import path from 'path';
import { RequestCache } from '@msdyn365-commerce/cache-internal';
import { IModuleBinder, IModuleBinderPartial } from '../app-initialization/models';
import { loadData } from '../data/load-data';
import { CLIENT_ENTRY_POINTS_TYPE } from '../consts';
import { optimizedGetAllModules } from '../utils/helpers';

export type TAsyncClientRenderComponentProps = IGeneric<IAny> & {
    typeName: string;
    requestCache: RequestCache;
    renderModuleAttributes: (props: IGeneric<IAny>) => IDictionary<string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    componentRef: React.RefObject<React.Component<{}, any>>;
    renderView: React.FC<IModule>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mappedProps: any;
    lazyLoadHandler: () => void;
    scriptInitCompleted: boolean;
};

/**
 *
 */
export class AsyncClientRenderComponent extends React.Component<TAsyncClientRenderComponentProps> {
    constructor(props) {
        super(props);
    }

    public loadScriptAsync = async (moduleChunk: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            document.body.appendChild(script);
            script.type = 'text/javascript';
            script.onload = () => {
                resolve();
            };
            script.onerror = e => {
                console.error(`failed to load module chunk :: ${moduleChunk}`, e);
            };
            script.src = `${moduleChunk}`;
            script.async = false;
            script.defer = false;
        });
    };

    public waitAndInitializeModule = async (retryCount: number): Promise<void> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bindings = (window as any).__bindings__;
        const registeredModules = bindings.modules;

        // lets make sure the script execution is complete before attemtping to render the module
        if (!registeredModules[this.props.mappedProps.typeName] && retryCount < 5) {
            setTimeout(() => this.waitAndInitializeModule(++retryCount), 500);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pageContext = (window as any).__msdyn365__.pageContext;
        let allModules: IModule[] = [];
        if (pageContext?.requestContext?.features?.loadData_optimization) {
            allModules = await optimizedGetAllModules(pageContext, pageContext.requestContext);
        } else {
            allModules = await pageContext.allModules(pageContext.requestContext, []);
        }
        const currentModuleArray = allModules.filter(m => m.typeName === this.props.mappedProps.typeName);
        const currentModule = currentModuleArray && currentModuleArray.length > 0 ? currentModuleArray[0] : undefined;

        if (!currentModule) {
            return;
        }

        // reassign the data actions from window bindings to reflect new data actions from client module
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (msdyn365Commerce as any).bindings.dataActions = bindings.dataActions;
        await loadData(
            [currentModule],
            this.props.mappedProps.context.request,
            this.props.mappedProps.internalTelemetry,
            this.props.mappedProps.internalTelemetry,
            this.props.requestCache,
            null,
            []
        );
        this.props.lazyLoadHandler();
    };

    public componentDidMount = async (): Promise<void> => {
        if (msdyn365Commerce.isBrowser) {
            if (this.props.scriptInitCompleted) {
                return this.waitAndInitializeModule(0);
            }

            const clientModuleChunks = this.props.requestCache.get<string[]>({
                typeName: CLIENT_ENTRY_POINTS_TYPE,
                key: this.props.typeName
            })
                ? this.props.requestCache.get<string[]>({ typeName: CLIENT_ENTRY_POINTS_TYPE, key: this.props.typeName })?.item
                : [];

            if (!clientModuleChunks || clientModuleChunks.length === 0) {
                return this.waitAndInitializeModule(0);
            } else {
                const chunksToBeAdded: string[] = [];
                clientModuleChunks?.map(chunk => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const scriptElements = document.getElementsByTagName('script') as any;
                    let chunkExistsOnPage = false;

                    for (const scriptEl of scriptElements) {
                        if (scriptEl.src.includes(chunk)) {
                            chunkExistsOnPage = true;
                        }
                    }

                    if (!chunkExistsOnPage) {
                        chunksToBeAdded.push(chunk);
                    }
                });

                if (chunksToBeAdded.length === 0) {
                    // if chunk exists on page, we can render the module with wait time for script execution to avoid race condition(in case if the script exeuction is incomplete)
                    return this.waitAndInitializeModule(0);
                }

                // add the chunks to the page and render module after script load event
                await Promise.all(
                    (chunksToBeAdded || []).map(async moduleChunk => {
                        return this.loadScriptAsync(moduleChunk);
                    })
                );

                return this.waitAndInitializeModule(0);
            }
        }
    };

    public render = (): React.ReactNode | null => {
        if (!this.props.scriptInitCompleted) {
            return <div />;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const registrationBindings = (window as any).__bindings__;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const registeredModules = (msdyn365Commerce as any).bindings.modules;

        if (!registrationBindings.modules[this.props.typeName]) {
            return <div />;
        }

        Object.keys(registrationBindings.modules || []).map(moduleTypeName => {
            if (!registeredModules[moduleTypeName]) {
                const rawModuleRegistrationObj: IModuleBinderPartial = registrationBindings.modules[moduleTypeName];
                const newModuleRegistrationObj: IModuleBinder = {} as IModuleBinder;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const fetchComponent = rawModuleRegistrationObj.c() as any;
                newModuleRegistrationObj.component = fetchComponent.default;
                newModuleRegistrationObj.moduleDirectory = rawModuleRegistrationObj.md;
                newModuleRegistrationObj.dataActions = rawModuleRegistrationObj.da;
                newModuleRegistrationObj.packageName = rawModuleRegistrationObj.p;
                newModuleRegistrationObj.name = rawModuleRegistrationObj.n;
                newModuleRegistrationObj.definitionPath = path.join(
                    rawModuleRegistrationObj.md,
                    `${rawModuleRegistrationObj.n}.definition.json`
                );
                newModuleRegistrationObj.parentDefinitionPath = rawModuleRegistrationObj.pdp;
                newModuleRegistrationObj.isNodeModule = rawModuleRegistrationObj.iNM;
                newModuleRegistrationObj.moduleNamespace = rawModuleRegistrationObj.ns;
                newModuleRegistrationObj.chunkName = `${rawModuleRegistrationObj.ns}-${rawModuleRegistrationObj.p}-${rawModuleRegistrationObj.n}`;
                registeredModules[moduleTypeName] = newModuleRegistrationObj;
            }
        });

        const Component = registeredModules[this.props.typeName].component;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const registeredViews = (msdyn365Commerce as any).bindings.views;
        Object.keys(registrationBindings.viewDictionary || []).map(viewRegistrationKey => {
            if (!registeredViews[viewRegistrationKey]) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const rawViewRegistrationObj: any = registrationBindings.viewDictionary[viewRegistrationKey];
                const newViewComponent = rawViewRegistrationObj.c();
                registeredViews[viewRegistrationKey] = {};
                registeredViews[viewRegistrationKey].component = newViewComponent.default;
            }
        });

        return (
            <Component
                {...this.props.mappedProps}
                ref={this.props.componentRef}
                {...{ renderModuleAttributes: this.props.renderModuleAttributes, renderView: this.props.renderView }}
            />
        );
    };
}
